// Shared cart cache: multiple shop-the-look forms initialize concurrently
// on PDP load; without this each one fired its own /cart.js request. Short
// TTL is enough to coalesce the burst of init-time fetches into one.
const _cartCache = { promise: null, ts: 0, ttl: 800 };
function getCart(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && _cartCache.promise && now - _cartCache.ts < _cartCache.ttl) {
    return _cartCache.promise;
  }
  _cartCache.ts = now;
  _cartCache.promise = fetch(`${routes.cart_url}.js`).then((r) => r.json());
  return _cartCache.promise;
}
function invalidateCartCache() {
  _cartCache.promise = null;
  _cartCache.ts = 0;
}

// When a Shop the Look bundle is added, the cart contains ONE line item
// (the bundle parent) with properties._bundle_id + _bundle_complete='true',
// and has_components: true. Components aren't exposed in cart.js. So a plain
// variant_id scan misses every component except the one that happens to be
// the parent's own variant_id.
//
// Caller passes the current page's bundle id (from data-bundle-id). If any
// cart line item belongs to the same complete bundle, every component of that
// bundle is considered "in cart." Top-level variant matches still win for
// non-bundle items.
function cartContainsVariant(cart, variantId, bundleId) {
  const targetVariant = String(variantId);
  return cart.items.some((item) => {
    if (String(item.variant_id) === targetVariant) return true;
    if (
      bundleId &&
      item.properties &&
      item.properties._bundle_id === bundleId &&
      item.properties._bundle_complete === 'true'
    ) {
      return true;
    }
    return false;
  });
}

class ShopTheLookVariants extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.productImage = this.closest('.shop-the-look__product').querySelector('.shop-the-look__product-image img');
    this.productId = this.dataset.productId;
    this.form = this.closest('.shop-the-look__product').querySelector('form');

    // Initialize variant on construction
    this.initializeVariant();

    // Add event listeners for radio buttons
    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', (event) => {
        this.onVariantChange(event);
      });
    });

    // Add event listeners for select elements
    this.querySelectorAll('select.select__select').forEach((select) => {
      select.addEventListener('change', (event) => {
        // If placeholder is selected, return
        if (!event.target.value) {
          return;
        }
        this.onVariantChange(event);
      });
    });
  }

  initializeVariant() {
    const fieldsets = this.querySelectorAll('fieldset');
    const variantData = this.getVariantData();
    const initialVariantId = parseInt(this.dataset.initialVariantId);

    // Find the initial variant
    const initialVariant = variantData.find((variant) => variant.id === initialVariantId);

    // If initial variant is available, use it
    if (initialVariant && initialVariant.available) {
      this.currentVariant = initialVariant;
      this.selectVariant(initialVariant, fieldsets);
      return;
    }

    // If initial variant not found or not available, find first available variant
    const availableVariants = variantData.filter((variant) => variant.available);

    if (availableVariants.length > 0) {
      this.currentVariant = availableVariants[0];
      this.selectVariant(availableVariants[0], fieldsets);
    } else {
      this.currentVariant = variantData[0];
      this.selectVariant(variantData[0], fieldsets);
    }

    // Ensure we update the form's variant ID
    if (this.currentVariant && this.form) {
      const variantInput = this.form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = this.currentVariant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Force an availability UI update
    this.updateAvailabilityUI();
  }

  selectVariant(variant, fieldsets) {
    if (!variant) return;

    const selectedOptions = [];

    // For each fieldset, select the option matching the variant
    fieldsets.forEach((fieldset, index) => {
      const optionValue = variant.options[index];

      // Check for radio buttons first
      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      if (radioInputs.length > 0) {
        const radioToSelect = Array.from(radioInputs).find((radio) => radio.value === optionValue);
        if (radioToSelect) {
          radioToSelect.checked = true;
          selectedOptions.push(optionValue);
        }
      }

      // Check for select elements
      const selectInput = fieldset.querySelector('select');
      if (selectInput) {
        const optionToSelect = Array.from(selectInput.options).find((option) => option.value === optionValue);
        if (optionToSelect) {
          selectInput.value = optionValue;
          selectedOptions.push(optionValue);
        }
      }
    });

    // Update variant state
    this.options = selectedOptions;

    // Update UI
    this.updateProductImage();
    this.updateAvailabilityUI();

    // Trigger price update with product-specific event
    document.dispatchEvent(
      new CustomEvent('variant-changed', {
        detail: {
          productId: this.productId,
          variant: variant,
        },
      })
    );
  }

  onVariantChange(event) {
    // If this is a radio button change, handle it directly
    if (event?.target?.type === 'radio') {
      const newVariantId = event.target.getAttribute('data-variant-id');

      // Find the variant in our data
      const variantData = this.getVariantData();
      this.currentVariant = variantData.find((variant) => variant.id.toString() === newVariantId);
    } else {
      // For select changes, we need to:
      // 1. Update the options array
      this.updateOptions();

      // 2. Find the matching variant
      const variantData = this.getVariantData();

      // Find a variant that matches our selected options
      this.currentVariant = variantData.find((variant) => {
        // Only consider available variants
        if (!variant.available) {
          return false;
        }

        // Compare each option in the correct order
        for (let i = 0; i < variant.options.length; i++) {
          const variantOption = variant.options[i];
          const selectedOption = this.options[i];
          // Skip comparison if selectedOption is null (unselected)
          if (selectedOption === null) {
            continue;
          }

          // Strict matching for selected options
          if (selectedOption !== variantOption) {
            return false;
          }
        }
        return true;
      });
    }

    // Get the form and variant input for status updates
    const form = this.form;
    const variantInput = form?.querySelector('input[name="id"]');

    // Update the form's variant ID if we found a valid variant
    if (this.currentVariant && form) {
      variantInput.value = this.currentVariant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update URL and image
    this.updateURL();
    this.updateProductImage();

    // Always update availability UI, even if we don't have a current variant
    this.updateAvailabilityUI();

    // Dispatch variant change event if we have a variant
    if (this.currentVariant) {
      document.dispatchEvent(
        new CustomEvent('variant-changed', {
          detail: {
            productId: this.productId,
            variant: this.currentVariant,
          },
        })
      );
    }

  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset, .product-form__input'));
    const variantData = this.getVariantData();

    // Initialize options array with the correct size
    const optionCount = variantData[0]?.options?.length || 3;
    this.options = new Array(optionCount).fill(null);

    // First, map each fieldset to its correct option position
    fieldsets.forEach((fieldset) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      const select = fieldset.querySelector('select');
      let optionPosition = -1;

      if (radios.length > 0) {
        // For radio buttons, use the data-option-position attribute
        const checkedRadio = Array.from(radios).find((radio) => radio.checked);
        if (checkedRadio) {
          optionPosition = parseInt(checkedRadio.getAttribute('data-option-position')) - 1;
          this.options[optionPosition] = checkedRadio.value;
        }
      }

      if (select && select.name) {
        // For select elements, find the matching option position by comparing option names
        const optionName = select.name.replace('options[', '').replace(']', '');

        // For size, we know it's always position 1 (second position)
        if (optionName.toLowerCase().includes('size')) {
          optionPosition = 1;
        } else {
          // For other selects, try to find position by matching against variant options
          optionPosition = variantData[0].options.findIndex(
            (opt) =>
              opt.toLowerCase().includes(optionName.toLowerCase()) ||
              optionName.toLowerCase().includes(opt.toLowerCase())
          );
        }

        // If we have a valid position and a selected value, use it
        if (optionPosition !== -1 && select.value) {
          this.options[optionPosition] = select.value;
        }
      }
    });
  }

  updateMasterId() {
    const variantData = this.getVariantData();

    // Only update if we don't already have a current variant
    if (!this.currentVariant) {
      this.currentVariant = variantData.find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    // Update the form's variant ID input
    if (this.currentVariant && this.form) {
      const variantInput = this.form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = this.currentVariant.id;
        // Dispatch change event
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Update availability UI
    this.updateAvailabilityUI();
  }

  updateAvailabilityUI() {
    // Update add button
    const form = this.closest('.shop-the-look__product').querySelector('shop-the-look-form');
    if (form) {
      const addButton = form.querySelector('[type="submit"]');
      const addButtonText = addButton.querySelector('span');

      // Get current variant availability
      const isAvailable = this.currentVariant?.available === true;

      if (!isAvailable) {
        addButton.setAttribute('disabled', '');
        addButtonText.textContent = window.variantStrings?.soldOut || 'Sold Out';
      } else if (this.currentVariant) {
        addButton.removeAttribute('disabled');
        addButtonText.textContent = 'Add';
      }
    }

    // Update variant selectors
    const fieldsets = this.querySelectorAll('fieldset');
    const variantData = this.getVariantData();
    const selectedOptions = [...this.options]; // Current selected options

    fieldsets.forEach((fieldset) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      const select = fieldset.querySelector('select');

      // Determine the option position for this fieldset
      let optionPosition = -1;
      if (radios.length > 0) {
        optionPosition = parseInt(radios[0].getAttribute('data-option-position')) - 1;
      } else if (select) {
        const optionName = select.name.replace('options[', '').replace(']', '');
        if (variantData[0]) {
          optionPosition = variantData[0].options.findIndex((opt) => {
            const optLower = opt.toLowerCase();
            const nameLower = optionName.toLowerCase();
            return optLower.includes(nameLower) || nameLower.includes(optLower);
          });
        }
      }

      if (optionPosition === -1) {
        console.warn('Could not determine option position for fieldset:', fieldset);
        return;
      }

      if (radios.length) {
        radios.forEach((radio) => {
          // Create a copy of current selections to test this option
          const testOptions = [...selectedOptions];
          testOptions[optionPosition] = radio.value;

          // Check if any variant exists with these options
          const hasAvailableVariant = variantData.some((variant) => {
            // First check if this variant matches our test options
            const optionsMatch = testOptions.every((option, index) => {
              return !option || option === variant.options[index];
            });

            return optionsMatch && variant.available;
          });

          const label = fieldset.querySelector(`label[for="${radio.id}"]`);
          if (!hasAvailableVariant) {
            radio.classList.add('disabled');
            radio.disabled = true;
            label?.classList.add('disabled');
          } else {
            radio.classList.remove('disabled');
            radio.disabled = false;
            label?.classList.remove('disabled');
          }
        });
      }

      if (select) {
        Array.from(select.options).forEach((option) => {
          if (!option.value) return; // Skip placeholder option

          // Create a copy of current selections to test this option
          const testOptions = [...selectedOptions];
          testOptions[optionPosition] = option.value;

          // Check if any variant exists with these options
          const hasAvailableVariant = variantData.some((variant) => {
            // First check if this variant matches our test options
            const optionsMatch = testOptions.every((option, index) => {
              return !option || option === variant.options[index];
            });

            return optionsMatch && variant.available;
          });

          if (!hasAvailableVariant) {
            option.classList.add('disabled');
            option.disabled = true;
          } else {
            option.classList.remove('disabled');
            option.disabled = false;
          }
        });
      }
    });
  }

  updateProductImage() {
    if (!this.currentVariant || !this.currentVariant.featured_media) {
      return;
    }

    if (this.productImage) {
      const newImage = this.currentVariant.featured_media;

      this.productImage.src = newImage.preview_image.src;
      this.productImage.srcset = newImage.preview_image.src;
      this.productImage.alt = newImage.alt || '';
    }
  }

  updateURL() {
    if (this.dataset.updateUrl === 'false') return;

    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const product = this.closest('.shop-the-look__product');
    if (!product) return;

    const form = product.querySelector('shop-the-look-form');
    if (!form) return;

    const submitButton = form.querySelector('[type="submit"]');
    if (!submitButton) return;

    const buttonText = submitButton.querySelector('span');
    if (!buttonText) return;

    buttonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
    submitButton.setAttribute('disabled', '');

    // Only try to update price if the element exists
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (price) {
      price.classList.add('visibility-hidden');
    }
  }

  getVariantData() {
    try {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    } catch (error) {
      console.error('Error parsing variant data:', error);
      return [];
    }
  }
}

class ShopTheLookForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.spinner = this.querySelector('.loading-overlay__spinner');
    this.form.querySelector('[name=id]').disabled = false;
    this.cart = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    this.submitButton = this.querySelector('[type="submit"]');

    // Change to toggle event handler
    this.form.addEventListener('submit', this.onToggleCart.bind(this));

    // Listen for cart updates from any source
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Always check cart state on any cart update
      this.checkCartState();
    });

    // Listen for variant changes
    document.addEventListener('variant-changed', (event) => {
      const productId = this.form.querySelector('input[data-product-id]')?.getAttribute('data-product-id');
      // Only update if this is our product
      if (event.detail.productId === productId) {
        // Update the form's variant ID input
        const variantInput = this.form.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = event.detail.variant.id;
        }
        this.checkCartState();
      }
    });

    // Also listen for direct changes to the variant input
    const variantInput = this.form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', () => {
        this.checkCartState();
      });
    }

    // Check initial cart state
    this.checkCartState();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async checkCartState() {
    try {
      const cart = await getCart();

      // Get the current variant ID directly from the form input
      const currentVariantId = this.form.querySelector('input[name="id"]').value;
      const bundleId = this.closest('.shop-the-look__container')?.dataset.bundleId;

      // Check if this exact variant ID is in the cart
      const isInCart = cartContainsVariant(cart, currentVariantId, bundleId);

      this.updateButtonState(isInCart);
    } catch (error) {
      console.error('Error checking cart state:', error);
    }
  }

  updateButtonState(isInCart) {
    const buttonText = this.submitButton.querySelector('span');
    if (isInCart) {
      this.submitButton.classList.add('is-in-cart');
      buttonText.textContent = 'Remove';
    } else {
      this.submitButton.classList.remove('is-in-cart');
      buttonText.textContent = 'Add';
    }
  }

  onCartUpdate(event) {
    this.checkCartState();
  }

  async onToggleCart(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');

    const isRemoving = this.submitButton.classList.contains('is-in-cart');

    try {
      if (isRemoving) {
        await this.removeFromCart();
      } else {
        await this.addToCart();
      }
    } catch (error) {
      this.handleErrorMessage(error.message);
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
    }
  }
  // orign code
  // async addToCart() {
  //   if (!this.form || !this.button) return;

  //   // Show loading state
  //   this.button.classList.add('loading');
  //   this.button.setAttribute('disabled', '');

  //   try {
  //     const formData = new FormData(this.form);
  //     const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
  //       method: 'POST',
  //       body: formData,
  //     });

  //     if (!response.ok) {
  //       throw new Error('Network response was not ok');
  //     }

  //     const responseData = await response.json();
  //     this.updateButtonState(true);

  //     // Dispatch cart update event
  //     document.dispatchEvent(new CustomEvent('cart:item-added', { detail: responseData }));
  //   } catch (error) {
  //     console.error('Error:', error);
  //     this.handleErrorMessage(error.message);
  //   } finally {
  //     // Hide loading state
  //     this.button.classList.remove('loading');
  //     this.button.removeAttribute('disabled');
  //   }
  // }


    async addToCart() {
    if (!this.form || !this.button) return;

    try {
      // loading state
      this.button.classList.add('loading');
      this.button.setAttribute('disabled', '');

      const formData = new FormData(this.form);

      // Add bundle properties for discount function
      const bundleContainer = this.closest('.shop-the-look__container');
      if (bundleContainer) {
        const bundleId = bundleContainer.dataset.bundleId || '';
        const bundleSize = bundleContainer.dataset.bundleSize || '';
        if (bundleId) {
          formData.append('properties[_bundle_id]', bundleId);
          formData.append('properties[_bundle_size]', bundleSize);
          formData.append('properties[_bundle_title]', bundleContainer.dataset.bundleTitle || 'Shop the Look');
          if (bundleContainer.dataset.bundleImage) {
            formData.append('properties[_bundle_image]', bundleContainer.dataset.bundleImage);
          }
        }
      }

      /* ---------------------------
        ADD ITEM TO CART
      ----------------------------*/
      const response = await fetch(
        window.Shopify.routes.root + 'cart/add.js',
        {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const responseData = await response.json();

      /* ---------------------------
        UPDATE BUTTON STATE
      ----------------------------*/
      this.updateButtonState(true);

      /* ---------------------------
        REFRESH CART + OPEN DRAWER
      ----------------------------*/
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        // fetch updated cart + drawer HTML in parallel
        const [drawerHtml, updatedCart] = await Promise.all([
          fetch(`?section_id=cart-drawer`).then(r => r.text()),
          fetch(`${routes.cart_url}.js`).then(r => r.json())
        ]);

        // update drawer DOM
        await updateCartDrawer(cartDrawer, drawerHtml, updatedCart);

        // open drawer (Dawn native method)
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('active');
        }
      }

      /* ---------------------------
        PUBLISH CART EVENT
      ----------------------------*/
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'shop-the-look-form',
        productVariantId: responseData.id,
        action: 'add'
      });

    } catch (error) {
      console.error('Add to cart error:', error);
      this.handleErrorMessage(error.message);
    } finally {
      // remove loading state
      this.button.classList.remove('loading');
      this.button.removeAttribute('disabled');
    }
  }
  
  async removeFromCart() {
    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();

      const variantId = this.form.querySelector('input[name="id"]').value;
      if (!variantId) {
        throw new Error('No variant ID found');
      }

      // Find the item key instead of line number
      const cartItem = cart.items.find((item) => item.variant_id.toString() === variantId);
      if (!cartItem) {
        throw new Error('Item not found in cart');
      }

      // Use the item key for reliable removal
      const updates = {
        [cartItem.key]: 0,
      };

      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: ['cart-drawer'],
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Failed to remove item from cart');
      }

      // Get updated cart state
      const updatedCartResponse = await fetch(`${routes.cart_url}.js`);
      const updatedCart = await updatedCartResponse.json();

      // Get updated drawer HTML
      const drawerResponse = await fetch(`?section_id=cart-drawer`);
      const drawerHtml = await drawerResponse.text();

      // Update cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        await updateCartDrawer(cartDrawer, drawerHtml, updatedCart);
      }

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'shop-the-look-form',
        productVariantId: variantId,
        action: 'remove',
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  updateVariantId(newId) {
    this.variantId = newId;
    this.checkCartState();
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;

    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

class ShopTheLookBundleForm extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.spinner = this.querySelector('.loading-overlay__spinner');
    this.button.addEventListener('click', this.onToggleBundle.bind(this));
    this.cart = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    this.totalPriceElement = this.querySelector('.bundle-total-price');
    this.fullPriceElement = this.closest('.shop-the-look__products-content')?.querySelector('.bundle-full-price');
    this.addToCartButton = this.querySelector('#bundle-add-to-cart');

    // Initialize with a loading state
    if (this.totalPriceElement) {
      this.totalPriceElement.textContent = 'Calculating...';
    }

    // Calculate initial price immediately
    this.calculateInitialPrice().then(() => {
      // After initial price is calculated, set up other listeners
      document.addEventListener('variant-changed', () => {
        this.updateBundlePrice();
        this.checkBundleState();
      });

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.checkBundleState();
        this.updateBundlePrice();
      });

      this.checkBundleState();
    });

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', async (event) => {
        event.preventDefault();
        this.handleAddToCart();
      });
    }
  }

  async calculateInitialPrice() {
    try {
      await this.updateBundlePrice();
    } catch (error) {
      console.error('Error calculating initial price:', error);
      if (this.totalPriceElement) {
        this.totalPriceElement.textContent = 'Price unavailable';
      }
    }
  }

  async updateBundlePrice() {
    try {
      // Get current cart state (shared cache coalesces concurrent calls)
      const cart = await getCart();

      let total = 0;
      let totalInCart = 0;
      const productForms = this.closest('.shop-the-look').querySelectorAll('shop-the-look-form form');
      const bundleId = this.closest('.shop-the-look__container')?.dataset.bundleId;

      for (const form of productForms) {
        const variantId = form.querySelector('input[name="id"]').value;
        const submitButton = form.querySelector('[type="submit"]');
        const priceElement = form
          .closest('.shop-the-look__product')
          .querySelector('.price__regular .price-item--regular');

        // Skip if variant is not available or no price element
        if (submitButton.hasAttribute('disabled') || !priceElement) continue;

        const price =
          priceElement.getAttribute('data-regular-price') || priceElement.textContent.replace(/[^0-9.]/g, '');

        if (!price) continue;

        const priceInCents = parseFloat(price) * 100;

        // Check if this variant is in cart
        const isInCart = cartContainsVariant(cart, variantId, bundleId);

        // Add to appropriate total
        if (isInCart) {
          totalInCart += priceInCents;
        }
        total += priceInCents;
      }

      // Calculate remaining total (items not in cart)
      const remainingTotal = total - totalInCart;

      // Update "Typically" full price
      if (this.fullPriceElement) {
        this.fullPriceElement.textContent = this.formatMoney(total);
      }

      // Update button price with 10% discount
      const discountedRemaining = Math.round(remainingTotal * 0.9);
      if (this.totalPriceElement) {
        this.totalPriceElement.textContent = this.formatMoney(discountedRemaining);
      }
      return { total, totalInCart, remainingTotal };
    } catch (error) {
      console.error('Error updating bundle price:', error);
      if (this.totalPriceElement) {
        this.totalPriceElement.textContent = 'Price unavailable';
      }
      throw error;
    }
  }

  formatMoney(cents) {
    const moneyFormat =
      document.querySelector('[data-shop-currency]')?.getAttribute('data-shop-currency') || '${{amount}}';

    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0.00';

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    };

    const value = formatWithDelimiters(cents, 2);
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async checkBundleState() {
    try {
      const cart = await getCart();

      // Get all product forms in the bundle
      const productForms = this.closest('.shop-the-look').querySelectorAll('shop-the-look-form form');
      const bundleId = this.closest('.shop-the-look__container')?.dataset.bundleId;

      // Get all available products and their cart status
      const products = Array.from(productForms)
        .map((form) => {
          const variantId = form.querySelector('input[name="id"]')?.value?.toString();
          const submitButton = form.querySelector('[type="submit"]');
          const isAvailable = !submitButton.hasAttribute('disabled');
          const isInCart = cartContainsVariant(cart, variantId, bundleId);

          return variantId ? { variantId, isAvailable, isInCart } : null;
        })
        .filter(Boolean);

      // Count available and cart items
      const availableProducts = products.filter((p) => p.isAvailable);
      const productsInCart = products.filter((p) => p.isAvailable && p.isInCart);

      const allInCart = productsInCart.length === availableProducts.length && availableProducts.length > 0;
      const someInCart = productsInCart.length > 0 && productsInCart.length < availableProducts.length;

      // Update button state and trigger price update
      await this.updateBundlePrice();
      this.updateButtonState(allInCart, someInCart);
    } catch (error) {
      console.error('Error checking bundle state:', error);
    }
  }

  updateButtonState(allInCart, someInCart) {
    const buttonText = this.button.querySelector('.bundle-button-text');

    if (allInCart) {
      this.button.classList.add('is-in-cart');
      buttonText.textContent = 'Remove selected from cart';
    } else {
      this.button.classList.remove('is-in-cart');
      if (someInCart) {
        buttonText.innerHTML = `Complete the look <span class="discount-label">10% Off</span> — &nbsp;<span class="bundle-total-price">${this.totalPriceElement.textContent}</span>`;
      } else {
        buttonText.innerHTML = `Get the look <span class="discount-label">10% Off</span>&nbsp; — &nbsp;<span class="bundle-total-price">${this.totalPriceElement.textContent}</span>`;
      }
    }
  }

  async onToggleBundle(evt) {
    evt.preventDefault();

    this.button.classList.add('loading');
    this.button.setAttribute('aria-disabled', true);

    try {
      const isRemoving = this.button.classList.contains('is-in-cart');

      if (isRemoving) {
        await this.removeBundle();
      } else {
        await this.addBundle();
      }
    } catch (error) {
      console.error(error);
      this.handleErrorMessage(error.message || window.cartStrings?.error || 'An error occurred');
    } finally {
      this.button.classList.remove('loading');
      this.button.removeAttribute('aria-disabled');
    }
  }

  async removeBundle() {
    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();

      // Get all variant IDs from the bundle forms
      const productForms = this.closest('.shop-the-look').querySelectorAll('shop-the-look-form form');
      const variantIds = Array.from(productForms)
        .map((form) => form.querySelector('input[name="id"]')?.value)
        .filter(Boolean)
        .map((id) => id.toString());

      // Create updates object using item keys for reliable removal
      const updates = {};
      cart.items.forEach((item) => {
        if (variantIds.includes(item.variant_id.toString())) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) {
        return;
      }

      // Remove all items in a single request
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: ['cart-drawer'],
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Failed to update cart');
      }

      const responseData = await response.json();

      // Get final cart state
      const updatedCartResponse = await fetch(`${routes.cart_url}.js`);
      const updatedCart = await updatedCartResponse.json();

      // Get updated drawer HTML
      const drawerResponse = await fetch(`?section_id=cart-drawer`);
      const drawerHtml = await drawerResponse.text();

      // Update cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        await updateCartDrawer(cartDrawer, drawerHtml, updatedCart);
      }

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'shop-the-look-bundle',
        action: 'remove',
        productVariantIds: variantIds,
      });

      // Force a state check after removal
      await this.checkBundleState();
    } catch (error) {
      console.error('Error removing bundle:', error);
      throw error;
    }
  }

  async addBundle() {
    if (!this.button) return;

    // Show loading state
    this.button.classList.add('loading');
    this.button.setAttribute('disabled', '');

    try {
      // Get current cart state first
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();

      // Get all product forms in the bundle
      const productForms = this.closest('.shop-the-look').querySelectorAll('shop-the-look-form form');
      const bundleId = this.closest('.shop-the-look__container')?.dataset.bundleId;
      const items = [];
      const unavailableItems = [];

      // Collect items from each product form
      productForms.forEach((form) => {
        const variantId = form.querySelector('input[name="id"]').value;
        const submitButton = form.querySelector('[type="submit"]');
        const productTitle = form
          .closest('.shop-the-look__product')
          .querySelector('.shop-the-look__product-title').textContent;

        // Check if variant is already in cart
        const isInCart = cartContainsVariant(cart, variantId, bundleId);

        // Only add if variant is available AND not already in cart
        if (!submitButton.hasAttribute('disabled') && !isInCart) {
          const bundleContainer = this.closest('.shop-the-look__container');
          const bundleId = bundleContainer?.dataset.bundleId || '';
          const bundleSize = bundleContainer?.dataset.bundleSize || '';
          const bundleProps = {
              _bundle_id: bundleId,
              _bundle_size: bundleSize,
              _bundle_title: bundleContainer?.dataset.bundleTitle || 'Shop the Look Bundle',
          };
          if (bundleContainer?.dataset.bundleImage) {
            bundleProps._bundle_image = bundleContainer.dataset.bundleImage;
          }
          items.push({
            id: parseInt(variantId),
            quantity: 1,
            properties: bundleProps,
          });
        } else if (submitButton.hasAttribute('disabled')) {
          unavailableItems.push({
            title: variantId,
            product_title: productTitle,
          });
        }
      });

      if (items.length === 0) {
        return;
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items: items,
        sections: ['cart-drawer'],
      });

      const response = await fetch(`${routes.cart_add_url}`, config);
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }

      // Get updated cart state
      const updatedCartResponse = await fetch(`${routes.cart_url}.js`);
      const cartState = await updatedCartResponse.json();

      // Get updated drawer HTML
      const drawerResponse = await fetch(`?section_id=cart-drawer`);
      const drawerHtml = await drawerResponse.text();

      // Update cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        await updateCartDrawer(cartDrawer, drawerHtml, cartState);
        cartDrawer.classList.add('active'); // Show the drawer
      }

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'shop-the-look-bundle',
        action: 'add',
        productVariantIds: items.map((item) => item.id),
      });

      // Force a state check after addition
      await this.checkBundleState();
    } catch (error) {
      console.error('Error:', error);
      this.handleErrorMessage(error.message);
    } finally {
      // Hide loading state
      this.button.removeAttribute('disabled');
      this.button.classList.remove('loading');
    }
  }

  handleErrorMessage(errorMessage = false) {
    if (!errorMessage) return;

    // Create or get error message container
    let errorContainer = this.querySelector('.shop-the-look__bundle-error');
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.classList.add('shop-the-look__bundle-error');
      this.button.insertAdjacentElement('afterend', errorContainer);
    }

    errorContainer.textContent = errorMessage;
    errorContainer.style.display = 'block';

    // Hide error after 5 seconds
    setTimeout(() => {
      errorContainer.style.display = 'none';
    }, 5000);
  }

  async handleAddToCart() {
    // Implementation of handleAddToCart method
  }
}

// Helper function to update cart drawer
async function updateCartDrawer(cartDrawer, drawerHtml, updatedCart) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(drawerHtml, 'text/html');

    // Update drawer content
    const newDrawerContent = doc.querySelector('cart-drawer-items');
    const newFooterContent = doc.querySelector('.drawer__footer');
    const newEmptyContent = doc.querySelector('.drawer__inner-empty');

    if (!cartDrawer) {
      console.error('Cart drawer not found');
      return;
    }

    // Remove existing empty state if it exists
    const existingEmpty = cartDrawer.querySelector('.drawer__inner-empty');
    if (existingEmpty) {
      existingEmpty.remove();
    }

    // Update content
    const existingDrawerItems = cartDrawer.querySelector('cart-drawer-items');
    const existingFooter = cartDrawer.querySelector('.drawer__footer');

    if (newDrawerContent && existingDrawerItems) {
      existingDrawerItems.replaceWith(newDrawerContent);
    }
    if (newFooterContent && existingFooter) {
      existingFooter.replaceWith(newFooterContent);
    }

    // Add empty state if cart is empty
    if (updatedCart.item_count === 0 && newEmptyContent) {
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.insertBefore(newEmptyContent, drawerInner.firstChild);
      }
    }

    // Update empty state class
    const isEmpty = updatedCart.item_count === 0;
    cartDrawer.classList.toggle('is-empty', isEmpty);

    // Update cart-drawer-items element
    const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.toggle('is-empty', isEmpty);
    }

    // Update cart counts
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      const countBubble = document.createElement('div');
      countBubble.classList.add('cart-count-bubble');
      // Get item count from the cart data response
      const itemCount = updatedCart.items ? updatedCart.items.length : 0;
      countBubble.innerHTML = `<span aria-hidden="true">${itemCount}</span><span class="visually-hidden">${itemCount} items</span>`;
      bubble.replaceWith(countBubble);
    });

    // Close drawer if cart is empty
    if (isEmpty && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }
  } catch (error) {
    console.error('Error updating cart drawer:', error);
    throw error;
  }
}

// Add styles to the page
const styles = `
  .shop-the-look__bundle-error,
  .shop-the-look__bundle-notification {
    margin-top: 1rem;
    padding: 1rem;
    border-radius: 4px;
    display: none;
  }

  .shop-the-look__bundle-error {
    background-color: #fee;
    color: #c00;
    border: 1px solid #fcc;
  }

  .shop-the-look__bundle-notification {
    background-color: #e8f4f8;
    color: #0066a4;
    border: 1px solid #bee5eb;
  }

  .shop-the-look__bundle-notification--warning {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeeba;
  }

  .shop-the-look__bundle-notification--success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
`;

const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

customElements.define('shop-the-look-variants', ShopTheLookVariants);
customElements.define('shop-the-look-form', ShopTheLookForm);
customElements.define('shop-the-look-bundle-form', ShopTheLookBundleForm);

// Any cart mutation (from this module or elsewhere) invalidates the shared cache
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
  subscribe(PUB_SUB_EVENTS.cartUpdate, invalidateCartCache);
}
